import { api } from './api';

/**
 * Cinch Phones
 * ------------
 * Users are read through our backend, which resolves the phone account from the company's
 * install — the account id is never passed from here. They are read-only: people are given
 * and removed from phone service in Cinch Phones itself.
 *
 * The location each user works out of is ours rather than the provider's, so those mappings
 * are an ordinary company-scoped resource keyed by `entity_ref` (the phone user's id).
 */

export function getCinchPhonesUsers(params) {
  return api.get(`/cinch-phones/users`, {
    params
  });
}
export function getCinchPhonesUserLocations(params) {
  return api.get(`/cinch-phones/user-locations`, {
    params
  });
}
export function createCinchPhonesUserLocation(data) {
  return api.post(`/cinch-phones/user-locations`, data);
}
export function updateCinchPhonesUserLocation(id, data) {
  return api.patch(`/cinch-phones/user-locations/${id}`, data);
}

/**
 * The phone numbers on the company's Cinch Phones account, each flagged with whether Cinch
 * already holds a record for it.
 *
 * Numbers belong to the account rather than to a person: a number can route to a dial plan
 * or ring group just as easily as to a user, so they are listed in their own right rather
 * than hung off a user.
 */
export function getCinchPhonesPhoneNumbers(params) {
  return api.get(`/cinch-phones/phone-numbers`, {
    params
  });
}

/**
 * Import the numbers Cinch doesn't hold yet, creating a voice-only phone record for each.
 * Idempotent — already-imported numbers are left alone, so this is safe to repeat.
 */
export function importCinchPhonesPhoneNumbers(data) {
  return api.post(`/cinch-phones/phone-numbers`, data);
}

/**
 * Put Bandwidth messaging on the given numbers, so they can send SMS while DialStack keeps
 * carrying their voice. One Letter of Authorization covers the whole request.
 *
 * Asynchronous — the numbers come back pending and go live when Bandwidth completes the
 * order, which can take days.
 */
export function hostCinchPhonesPhoneNumbers(data) {
  return api.post(`/cinch-phones/phone-numbers/host`, data);
}

/**
 * The phones you're allowed to dial from, each with the location it belongs to. Catalyst
 * users only get phones at their own locations.
 */
export function getCinchPhonesCallers(params) {
  return api.get(`/cinch-phones/calls`, {
    params
  });
}

/**
 * Click to dial. Rings the chosen phone first and connects it to `dial_string` once someone
 * answers — nothing reaches the customer until then.
 *
 * The backend re-checks that you may use that phone, so this can't be used to dial from
 * another location's. Resolves 202 (queued), not connected.
 */
export function createCinchPhonesCall(data) {
  return api.post(`/cinch-phones/calls`, data);
}

/**
 * A WebRTC session token so this browser can act as one of the company's phones — what
 * the in-browser softphone signs in with.
 *
 * The token can place and receive calls as that phone, so the backend re-checks that you
 * may use it. Resolves `{ token, expires_at, api_base_url, phone }`.
 */
export function createCinchPhonesSoftphoneToken(data) {
  return api.post(`/cinch-phones/webrtc-token`, data);
}